import { theme } from "@mindlercare/ui-web";
import { SlotStatus, TemplateName, TemplateNames } from "core/types/index";
import { P, match } from "ts-pattern";

type Args = {
  templateName: TemplateName;
  slotStatus: SlotStatus;
  isCouplesTherapyRevisit?: boolean;
};

export const isSlotCanceled = (status: SlotStatus) =>
  status === SlotStatus.CANCELED ||
  status === SlotStatus.LATE_CANCELED ||
  status === SlotStatus.NO_SHOW ||
  status === SlotStatus.ORGANIZER_NO_SHOW ||
  status === SlotStatus.NO_SHOW_ALL;

export const getSlotBackgroundColor = (args: Args) => {
  const varyingBackgroundTemplates = [
    TemplateNames.new_visit.toString(),
    TemplateNames.revisit.toString(),
    TemplateNames.short_visit.toString(),
    TemplateNames.advisory_consult.toString(),
    TemplateNames.emdr.toString(),
    TemplateNames.long_visit.toString(),
    TemplateNames.couples_therapy.toString(),
    TemplateNames.adhd_screening.toString(),
  ];
  const isCanceled = isSlotCanceled(args.slotStatus);
  return match({
    templateName: args.templateName,
    isCanceled,
    slotStatus: args.slotStatus,
    isCouplesTherapyRevisit: args.isCouplesTherapyRevisit,
  })
    .with(
      {
        templateName: TemplateNames.diagnostics_journal_entry,
        isCanceled: true,
      },
      () => theme.COLORS.BRAND_BLUE_OPACITY,
    )
    .with(
      {
        templateName: TemplateNames.diagnostics_journal_entry,
        isCanceled: false,
      },
      () => theme.COLORS.BRAND_BLUE,
    )
    .with(
      {
        templateName: TemplateNames.couples_therapy,
        isCouplesTherapyRevisit: true,
      },
      () => theme.COLORS.SECONDARY_LIGHT,
    )
    .with(
      {
        templateName: P.when((name) =>
          varyingBackgroundTemplates.includes(name),
        ),
        isCanceled: true,
      },
      () => theme.COLORS.TERTIARY_LIGHT_RED,
    )
    .with(
      {
        templateName: P.when((name) =>
          varyingBackgroundTemplates.includes(name),
        ),
        slotStatus: SlotStatus.OPEN,
      },
      () => theme.COLORS.WHITE,
    )
    .with(
      {
        templateName: P.when((name) =>
          (
            [
              TemplateNames.new_visit,
              TemplateNames.short_visit,
              TemplateNames.long_visit,
              TemplateNames.couples_therapy,
            ] as string[]
          ).includes(name),
        ),
        slotStatus: P.when((status) =>
          ([SlotStatus.BOOKED, SlotStatus.UNAVAILABLE] as string[]).includes(
            status,
          ),
        ),
      },
      () => theme.COLORS.TERTIARY_GREEN,
    )
    .with(
      {
        templateName: P.when((name) =>
          ([TemplateNames.adhd_screening] as string[]).includes(name),
        ),
        slotStatus: P.when((status) =>
          ([SlotStatus.BOOKED, SlotStatus.UNAVAILABLE] as string[]).includes(
            status,
          ),
        ),
      },
      () => theme.COLORS.TERTIARY_LIGHT,
    )
    .with(
      {
        templateName: P.when((name) =>
          (
            [
              TemplateNames.revisit,
              TemplateNames.emdr,
              TemplateNames.advisory_consult,
            ] as string[]
          ).includes(name),
        ),
        slotStatus: P.when((status) =>
          ([SlotStatus.BOOKED, SlotStatus.UNAVAILABLE] as string[]).includes(
            status,
          ),
        ),
      },
      () => theme.COLORS.SECONDARY_LIGHT,
    )
    .otherwise(() => theme.COLORS.MINDLER_LIGHT_GREY);
};
